/**
 * @module @yimoka/i18n/react/watch-lang
 * @summary 监听应用语言并 init i18next
 * @author keep
 * @since 2026-05-19
 * @modified 2026-05-21 移除 toI18nLng 端口（业务侧让 useLanguage 直接返回 i18next 期望的语言码）;支持 init 期间的 fallback 渲染
 */

import { observer } from '@yimoka/react';
import type { i18n } from 'i18next';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { initReactI18next } from 'react-i18next';

import type { I18nBackendPlugin, SyncManager } from '../types';

/** createWatchLang 配置 */
export interface CreateWatchLangOptions {
  i18n: i18n;
  /**
   * 业务语言 hook。
   *
   * @remarks 返回值必须与 bundled 顶层 key、`i18next.language` 形态一致（推荐 BCP 47 完整码：`zh-CN` / `zh-Hant` / `en-US`）;
   * 包内不再做任何语言码转换。若业务 store 存的码与 i18next 形态不一致，请在 useLanguage 内部完成转换。
   */
  useLanguage: () => string;
  getInitOptions: (lng: string) => Record<string, unknown>;
  backendModule: I18nBackendPlugin | null;
  syncManager: SyncManager;
  onLangChange?: (lang: string) => void;
}

/** WatchLang 组件 props */
export interface WatchLangProps {
  children: ReactNode;
  /** init 完成前展示的 UI，默认 null */
  fallback?: ReactNode;
}

/**
 * 创建 WatchLang 组件工厂。
 *
 * @param options - 配置
 * @returns React FC
 */
export function createWatchLang(options: CreateWatchLangOptions) {
  const {
    i18n,
    useLanguage,
    getInitOptions,
    backendModule,
    syncManager,
    onLangChange,
  } = options;

  let initPromise: Promise<void> | null = null;
  let initializedLang: string | null = null;

  const WatchLang = observer(({ children, fallback = null }: WatchLangProps) => {
    const lang = useLanguage();
    const [initLang, setInitLang] = useState(false);
    const initRef = useRef(false);
    const mountedRef = useRef(false);

    useEffect(() => {
      mountedRef.current = true;
      return () => {
        mountedRef.current = false;
      };
    }, []);

    useEffect(() => {
      let cancelled = false;
      if (initRef.current && initializedLang === lang) {
        return () => {
          cancelled = true;
        };
      }
      initRef.current = true;

      void (async () => {
        if (!initPromise) {
          initPromise = (async () => {
            if (!i18n.isInitialized) {
              if (backendModule) {
                await i18n.use(initReactI18next).use(backendModule)
                  .init(getInitOptions(lang));
              } else {
                await i18n.use(initReactI18next).init(getInitOptions(lang));
              }
            }
            await syncManager.start(i18n);
            initializedLang = i18n.resolvedLanguage ?? i18n.language ?? lang;
          })();
        }

        await initPromise;
        if (!cancelled && mountedRef.current) {
          setInitLang(true);
        }
      })();

      return () => {
        cancelled = true;
      };
    }, [lang]);

    useEffect(() => {
      if (!initLang) {
        return;
      }
      if ((i18n.resolvedLanguage ?? i18n.language) === lang) {
        onLangChange?.(lang);
        return;
      }
      let cancelled = false;
      void i18n.changeLanguage(lang).then(() => {
        if (cancelled || !mountedRef.current) {
          return;
        }
        initializedLang = i18n.resolvedLanguage ?? i18n.language ?? lang;
        onLangChange?.(lang);
      });
      return () => {
        cancelled = true;
      };
    }, [lang, initLang]);

    if (!initLang) {
      return fallback;
    }

    return children;
  });

  return WatchLang;
}
